import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput } from '../ui/Input'
import { fmt, uid } from '../../utils/formatters'

const TYPE_OPTIONS = [
    { value: 'bahan', label: '🧱 Bahan', color: 'var(--info)' },
    { value: 'upah', label: '👷 Upah', color: 'var(--success)' },
    { value: 'alat', label: '🔧 Alat', color: 'var(--purple)' },
]

export default function PresetManager({ show, onClose }) {
    const { user } = useAuth()
    const toast = useToast()
    const confirm = useConfirm()
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [filterType, setFilterType] = useState('bahan')
    const [showForm, setShowForm] = useState(false)
    const [editId, setEditId] = useState(null)
    const blank = { type: 'bahan', name: '', unit: '', price: 0 }
    const [form, setForm] = useState(blank)

    useEffect(() => {
        if (show && user) fetchItems()
    }, [show, user])

    const fetchItems = async () => {
        setLoading(true)
        const { data } = await supabase
            .from('preset_items')
            .select('*')
            .eq('user_id', user.id)
            .order('type')
            .order('name')
        if (data) setItems(data)
        setLoading(false)
    }

    const openAdd = () => {
        setForm({ ...blank, type: filterType })
        setEditId(null)
        setShowForm(true)
    }

    const openEdit = (item) => {
        setForm({ type: item.type, name: item.name, unit: item.unit, price: item.price })
        setEditId(item.id)
        setShowForm(true)
    }

    const save = async () => {
        if (!form.name.trim()) { toast.show('Nama wajib diisi', 'error'); return }
        const data = { ...form, price: parseInt(form.price) || 0, user_id: user.id }
        if (editId) {
            await supabase.from('preset_items').update(data).eq('id', editId)
        } else {
            await supabase.from('preset_items').insert([{ ...data, id: uid() }])
        }
        toast.show(editId ? 'Preset diperbarui' : 'Preset ditambahkan')
        setShowForm(false)
        fetchItems()
    }

    const del = async (id, name) => {
        const ok = await confirm(`Hapus preset "${name}"?`, {
            title: 'Hapus Preset', icon: '🗑️', confirmLabel: 'Hapus', confirmVariant: 'danger'
        })
        if (!ok) return
        await supabase.from('preset_items').delete().eq('id', id)
        toast.show('Preset dihapus')
        fetchItems()
    }

    const filtered = items.filter(i => i.type === filterType)

    if (!show) return null

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                style={{ maxWidth: 560, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="modal-header">
                    <h3>⚙️ Kelola Preset AHS</h3>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Type filter tabs */}
                <div style={{ display: 'flex', gap: 6, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
                    {TYPE_OPTIONS.map(t => (
                        <button
                            key={t.value}
                            className={`doc-type-chip${filterType === t.value ? ' active' : ''}`}
                            onClick={() => setFilterType(t.value)}
                        >
                            {t.label} ({items.filter(i => i.type === t.value).length})
                        </button>
                    ))}
                    <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={openAdd}>
                        ➕ Tambah
                    </button>
                </div>

                {/* Form inline */}
                {showForm && (
                    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', margin: '10px 16px', padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 10 }}>
                            {editId ? '✏️ Edit' : '➕ Tambah'} Item Preset
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Tipe</label>
                                <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                    {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Nama *</label>
                                <input
                                    className="form-input"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="Cth: Semen Putih 25kg"
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && save()}
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Satuan</label>
                                <input
                                    className="form-input"
                                    value={form.unit}
                                    onChange={e => setForm({ ...form, unit: e.target.value })}
                                    placeholder="sak / m² / OH / jam"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Harga Satuan</label>
                                <MoneyInput value={form.price} onChange={v => setForm({ ...form, price: v })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <Button size="sm" variant="secondary" onClick={() => setShowForm(false)}>Batal</Button>
                            <Button size="sm" onClick={save}>💾 Simpan</Button>
                        </div>
                    </div>
                )}

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Memuat...</div>
                    ) : filtered.length === 0 ? (
                        <div className="empty-state" style={{ padding: '24px 0' }}>
                            <div className="empty-icon" style={{ fontSize: 28 }}>📋</div>
                            <p>Belum ada preset {TYPE_OPTIONS.find(t => t.value === filterType)?.label}</p>
                            <Button size="sm" variant="primary" style={{ marginTop: 10 }} onClick={openAdd}>➕ Tambah Pertama</Button>
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                                    <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>Nama</th>
                                    <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600, width: 60 }}>Satuan</th>
                                    <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 600, width: 110 }}>Harga</th>
                                    <th style={{ width: 64 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td style={{ padding: '7px 4px', fontWeight: 500 }}>{item.name}</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'center', color: 'var(--text-muted)' }}>{item.unit}</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'right', color: 'var(--accent)', fontWeight: 700 }}>{fmt(item.price)}</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'right' }}>
                                            <button className="btn btn-secondary btn-xs" style={{ marginRight: 4 }} onClick={() => openEdit(item)}>✏️</button>
                                            <button className="btn btn-danger btn-xs" onClick={() => del(item.id, item.name)}>🗑️</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                    <Button variant="secondary" onClick={onClose}>Tutup</Button>
                </div>
            </div>
        </div>
    )
}
